import React, { useState } from 'react';
import { Upload, Download, FileCsv, X, CheckCircle, Warning } from '@phosphor-icons/react';
import DialogModal from './DialogModal';
import '../styles/CSVImportModal.css';

function CSVImportModal({
  isOpen,
  onClose,
  title = 'Import from CSV',
  entityName = 'records',
  requiredColumns = [],
  optionalColumns = [],
  staffTypeValues = null,
  onImport,
  onDownloadTemplate,
  importing = false,
  importProgress = null
}) {
  const [expandedSection, setExpandedSection] = useState('overview');
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [alertDialog, setAlertDialog] = useState(null); // { title, message, type }

  if (!isOpen) return null;

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    if (!file.name.endsWith('.csv')) {
      setAlertDialog({ title: 'Invalid File', message: 'Please select a valid CSV file', type: 'error' });
      return;
    }
    setSelectedFile(file);
    onImport(file);
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="csv-modal-overlay" onClick={onClose}>
      <div className="csv-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="csv-modal-header">
          <div className="header-content">
            <FileCsv size={28} weight="fill" className="header-icon" />
            <div>
              <h2>{title}</h2>
              <p>Follow these steps to import {entityName} data</p>
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="csv-modal-body">
          {/* Steps */}
          <div className="import-steps">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h4>Download Template</h4>
                <p>Get the CSV template with proper column headers</p>
                <button 
                  className="btn btn-outline btn-sm" 
                  onClick={onDownloadTemplate}
                  disabled={importing}
                >
                  <Download size={16} /> Download Template
                </button>
              </div>
            </div>

            <div className="step-arrow">→</div>

            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h4>Fill in Your Data</h4>
                <p>Add your {entityName} data following the format requirements</p>
              </div>
            </div>

            <div className="step-arrow">→</div>

            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h4>Upload File</h4>
                <p>Drag and drop or click to select your CSV file</p>
              </div>
            </div>
          </div>

          {/* File Upload Area */}
          <div 
            className={`file-upload-zone ${dragActive ? 'active' : ''} ${importing ? 'disabled' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {!importing ? (
              <>
                <Upload size={40} className="upload-icon" />
                <h3>Drag and drop your CSV file here</h3>
                <p>or</p>
                <label className="file-input-label">
                  Click to browse
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    disabled={importing}
                    style={{ display: 'none' }}
                  />
                </label>
                <p className="file-hint">CSV files only • Max 100MB</p>
                {selectedFile && (
                  <div className="selected-file">
                    <FileCsv size={16} />
                    <span>{selectedFile.name}</span>
                  </div>
                )}
              </>
            ) : importProgress ? (
              <div className="import-progress-detail">
                <div className="spinner"></div>
                <h3>Importing {entityName}...</h3>
                {importProgress.percentage && (
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${importProgress.percentage}%` }}
                    ></div>
                  </div>
                )}
                <p>{importProgress.message || 'Processing your file...'}</p>
              </div>
            ) : (
              <div className="import-progress-detail">
                <div className="spinner"></div>
                <h3>Importing {entityName}...</h3>
                <p>This may take a moment...</p>
              </div>
            )}
          </div>

          {/* Format Requirements (Collapsible) */}
          <div className="format-section">
            <button 
              className="section-header"
              onClick={() => toggleSection('required')}
            >
              <span>Required Columns ({requiredColumns.length})</span>
              <span className={`toggle-icon ${expandedSection === 'required' ? 'open' : ''}`}>▼</span>
            </button>
            {expandedSection === 'required' && (
              <div className="section-content">
                <div className="columns-list">
                  {requiredColumns.map((col, idx) => (
                    <div key={idx} className="column-item">
                      <div className="column-name">{col.name}</div>
                      <div className="column-desc">{col.description}</div>
                      {col.values && (
                        <div className="column-values">
                          <strong>Values:</strong> {col.values.join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Optional Columns (Collapsible) */}
          {optionalColumns.length > 0 && (
            <div className="format-section">
              <button 
                className="section-header"
                onClick={() => toggleSection('optional')}
              >
                <span>Optional Columns ({optionalColumns.length})</span>
                <span className={`toggle-icon ${expandedSection === 'optional' ? 'open' : ''}`}>▼</span>
              </button>
              {expandedSection === 'optional' && (
                <div className="section-content">
                  <div className="columns-list">
                    {optionalColumns.map((col, idx) => (
                      <div key={idx} className="column-item">
                        <div className="column-name">{col.name}</div>
                        <div className="column-desc">{col.description}</div>
                        {col.values && (
                          <div className="column-values">
                            <strong>Values:</strong> {col.values.join(', ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Staff Type Values (if provided) */}
          {staffTypeValues && (
            <div className="format-section">
              <button 
                className="section-header"
                onClick={() => toggleSection('staffTypes')}
              >
                <span>Staff Type Values</span>
                <span className={`toggle-icon ${expandedSection === 'staffTypes' ? 'open' : ''}`}>▼</span>
              </button>
              {expandedSection === 'staffTypes' && (
                <div className="section-content">
                  <div className="staff-types-list">
                    {Object.entries(staffTypeValues).map(([type, values]) => (
                      <div key={type} className="staff-type-item">
                        <strong>{type}:</strong> {values.join(' or ')}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Date Format Info */}
          <div className="format-section">
            <button 
              className="section-header"
              onClick={() => toggleSection('dates')}
            >
              <span>Supported Date Formats</span>
              <span className={`toggle-icon ${expandedSection === 'dates' ? 'open' : ''}`}>▼</span>
            </button>
            {expandedSection === 'dates' && (
              <div className="section-content">
                <div className="date-formats">
                  <div className="format-item recommended">
                    <CheckCircle size={16} />
                    <span><strong>YYYY-MM-DD</strong> (e.g., 2005-03-15) - Recommended</span>
                  </div>
                  <div className="format-item">
                    <span><strong>DD/MM/YYYY</strong> (e.g., 15/03/2005)</span>
                  </div>
                  <div className="format-item">
                    <span><strong>MM/DD/YYYY</strong> (e.g., 03/15/2005)</span>
                  </div>
                  <div className="format-item">
                    <span><strong>DD-MM-YYYY</strong> (e.g., 15-03-2005)</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Tips */}
          <div className="import-tips">
            <div className="tip">
              <Warning size={18} />
              <div>
                <strong>Important:</strong> Ensure all required columns have values. Empty cells in required columns will cause import errors.
              </div>
            </div>
            <div className="tip">
              <CheckCircle size={18} />
              <div>
                <strong>Tip:</strong> Use the template file to ensure your CSV matches the expected format.
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="csv-modal-footer">
          <button 
            className="btn btn-secondary"
            onClick={onClose}
            disabled={importing}
          >
            Cancel
          </button>
          <p className="footer-note">Upload a CSV file to get started</p>
        </div>

        <DialogModal
          isOpen={!!alertDialog}
          onClose={() => setAlertDialog(null)}
          title={alertDialog?.title}
          message={alertDialog?.message}
          type={alertDialog?.type || 'error'}
          primaryAction={{ label: 'OK', onClick: () => {} }}
        />
      </div>
    </div>
  );
}

export default CSVImportModal;
